import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { makeAutoObservable, runInAction } from 'mobx';
import { updateAOR } from 'api/operations/groupService';
import { getProducerListByAgency } from 'api/operations/userService';
import ModalDelegate from 'store/general/modalDelegate';
import { getRootStoreInstance } from 'store/rootStore';
import { InputAssignee } from 'types/groupService';
import { Producer } from 'types/userService';

export interface AgentOfRecordFormFields extends DefaultFormFields {
  agentOfRecord: FormControl<string>;
}

export class AgentOfRecordModalDelegate {
  private _rootStore = getRootStoreInstance();
  private _groupId = '';
  private _producers: Producer[] = [];

  public modalDelegate = new ModalDelegate();

  public form = new FormController<AgentOfRecordFormFields>(
    {
      agentOfRecord: new FormControl<string>(''),
    },
    {
      onSubmit: this.save.bind(this),
    },
  );

  public get agentOptions(): OptionType[] {
    const options = this._producers.map((agent) => ({
      value: agent.id,
      label: agent.name,
    }));
    const currentAor = this._rootStore.groupStore.group.agentOfRecord;
    const isCurrentInList = options.some((option) => option.value === currentAor?.id);

    if (currentAor?.id && !isCurrentInList) {
      options.push({ value: currentAor.id, label: currentAor.name });
    }

    return options;
  }

  private _buildAorInput(agentId: string): InputAssignee {
    const selectedProducer = this._producers.find((agent) => agent.id === agentId);
    if (!selectedProducer) {
      throw new Error('There is no agentOfRecord with such id!');
    }

    return {
      id: selectedProducer.id,
      name: selectedProducer.name,
      npn: selectedProducer.sponsorSpecificData?.npn,
      tin: selectedProducer.sponsorSpecificData?.tin,
      email: selectedProducer.sponsorSpecificData?.email,
    };
  }

  private async _loadProducers(agencyId: string): Promise<void> {
    this.form.setLoading(true);
    const producers = await getProducerListByAgency(agencyId, '');
    runInAction(() => {
      this._producers = producers;
      this.form.setLoading(false);
    });
  }

  public open(groupId: string): void {
    this._groupId = groupId;
    this.form.populateFields({
      agentOfRecord: this._rootStore.groupStore.group.agentOfRecord?.id || '',
    });
    this.modalDelegate.open();
    void this._loadProducers(this._rootStore.userStore.coreUserInfo.agencyId);
  }

  public close(): void {
    this.form.resetForm();
    this._producers = [];
    this.modalDelegate.close();
  }

  public async save(): Promise<void> {
    const aor = this._buildAorInput(this.form.fields.agentOfRecord.value);
    const result = await updateAOR(this._groupId, aor);
    runInAction(() => {
      this._rootStore.groupStore.group.agentOfRecord = result.agentOfRecord;
      this.modalDelegate.close();
    });
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
